<template>
  <Admin>
    <v-main>
      <v-container fluid class="tw-py-6">
        <v-card class="tw-p-6">
            <h5 class="tw-font-bold tw-text-xl tw-text-green-400 tw-tracking-wide tw-mt-2">สร้างบริษัท และ ผู้ใช้งาน</h5>
          <v-card-title class="tw-text-xl tw-font-semibold">Laboratory - Create Companies & Users</v-card-title>
          <v-card-text>
        <div v-if="!isAllowed" class="tw-text-red-600 tw-font-medium">
          คุณไม่มีสิทธิ์เข้าถึงหน้านี้
        </div>

        <div v-else>
          <v-form ref="form" v-model="valid">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.companyNameTH" label="Company Name (TH) Base" dense outlined></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.companyNameEN" label="Company Name (EN) Base" dense outlined></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field 
                  v-model="form.companyPrefix" 
                  label="Company ID Prefix" 
                  hint="Example: You Put prefix, You will get an company id like prefix001." 
                  persistent-hint 
                  :rules="[rules.required, rules.companyPrefixDuplicate]" 
                  :loading="validating.companyPrefix"
                  dense 
                  outlined
                  @input="debounceValidateCompanyPrefix"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field 
                  v-model="form.userPrefix" 
                  label="User ID Prefix" 
                  hint="Example: You Put prefix, You will get an user id like prefix001." 
                  persistent-hint 
                  :rules="[rules.required, rules.userPrefixDuplicate]" 
                  :loading="validating.userPrefix"
                  dense 
                  outlined
                  @input="debounceValidateUserPrefix"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field 
                  v-model="form.userEmailPrefix" 
                  label="User Email Prefix" 
                  hint="Example: You Put prefix, You will get an email like prefix001@gmail.com." 
                  persistent-hint 
                  :rules="[rules.userEmailPrefixDuplicate]" 
                  :loading="validating.userEmailPrefix"
                  dense 
                  outlined
                  @input="debounceValidateUserEmailPrefix"
                ></v-text-field>
              </v-col>
              <v-col cols="6" md="3">
                <v-text-field v-model.number="form.startIndex" type="number" min="1" label="Start No."  persistent-hint dense outlined></v-text-field>
              </v-col>
              <v-col cols="6" md="3">
                <v-text-field v-model.number="form.count" type="number" min="1" max="1000" label="Count" hint="Input Number you will to start Ex. 1 you will get 001" :rules="[rules.count]" dense outlined></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-menu
                  v-model="expireMenu"
                  :close-on-content-click="false"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="form.labExpireDate"
                      label="License หมดอายุ"
                      prepend-inner-icon="mdi-calendar-clock"
                      :hint="expireHint"
                      persistent-hint
                      :rules="[rules.required]"
                      readonly
                      dense
                      outlined
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="form.labExpireDate"
                    :min="minExpireDate"
                    no-title
                    color="#6ec39b"
                    @input="expireMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="6" class="tw-flex tw-items-center">
                <v-btn small text color="primary" @click="setExpireInMonths(1)">+1 เดือน</v-btn>
                <v-btn small text color="primary" @click="setExpireInMonths(3)">+3 เดือน</v-btn>
                <v-btn small text color="primary" @click="setExpireInMonths(6)">+6 เดือน</v-btn>
                <v-btn small text color="primary" @click="setExpireInMonths(12)">+1 ปี</v-btn>
              </v-col>
            </v-row>
            <v-btn color="primary" class="tw-mt-2" :loading="loading" :disabled="!valid || loading" @click="startRun">
              เริ่มต้นการสร้าง
            </v-btn>
          </v-form>
          
          <div v-if="runId" class="tw-mt-6">
            <v-alert :type="progress.status === 'failed' ? 'error' : 'info'" border="left" colored-border>
              <div class="tw-flex tw-items-center tw-justify-between">
                <div>
                  <div class="tw-font-semibold">Status: {{ progress.status || 'running' }}</div> 
                  <div>Step: {{ progress.step }}</div>
                  <div v-if="progress.message">{{ progress.message }}</div>
                </div>
                <div class="tw-w-56">
                  <v-progress-linear :value="progress.percent || 0" height="8" color="primary"></v-progress-linear>
                </div>
              </div>
            </v-alert>
            <div class="tw-text-sm tw-text-gray-500">Run ID: {{ runId }}</div>
          </div>
          </div>
        </v-card-text>
      </v-card>
    </v-container>
    </v-main>
  </Admin>
  
</template>

<script>
import Admin from '@/components/admin/admin-main-template'
import firebase from 'firebase/app'
import 'firebase/auth'
import 'firebase/firestore'
import fc from '@/firebaseConfig'
import {
  addMonthsToDateString,
  bangkokToday,
  defaultLabExpireDate,
  formatLabExpireDate,
  labLicenseDaysLeft,
} from '@/libs/labLicense'

export default {
  name: 'Laboratory',
  components: { Admin },
  data() {
    return {
      valid: true,
      loading: false,
      runId: '',
      unsubscribe: null,
      progress: {},
      expireMenu: false,
      form: {
        companyNameTH: '',
        companyNameEN: '',
        companyPrefix: '',
        userPrefix: '',
        userEmailPrefix: '',
        startIndex: 1,
        count: 1,
        // บริษัท Lab ชุดหนึ่งสร้างให้คอร์สหนึ่ง จึงตั้งวันหมดอายุไว้ตั้งแต่ตอนสร้าง
        // ค่าเริ่มต้น 1 เดือน ต่ออายุทีหลังได้ที่หน้าจัดการผลิตภัณฑ์ของบริษัทนั้น
        labExpireDate: defaultLabExpireDate(),
      },
      validating: {
        companyPrefix: false,
        userPrefix: false,
        userEmailPrefix: false,
      },
      validationErrors: {
        companyPrefix: null,
        userPrefix: null,
        userEmailPrefix: null,
      },
      debounceTimers: {
        companyPrefix: null,
        userPrefix: null,
        userEmailPrefix: null,
      },
      rules: {
        required: v => !!v || 'จำเป็นต้องกรอก',
        count: v => (v >= 1 && v <= 1000) || 'ต้องอยู่ระหว่าง 1-1000',
        companyPrefixDuplicate: v => {
          if (!v) return true
          return this.validationErrors.companyPrefix === null ? true : this.validationErrors.companyPrefix
        },
        userPrefixDuplicate: v => {
          if (!v) return true
          return this.validationErrors.userPrefix === null ? true : this.validationErrors.userPrefix
        },
        userEmailPrefixDuplicate: v => {
          if (!v) return true
          return this.validationErrors.userEmailPrefix === null ? true : this.validationErrors.userEmailPrefix
        },
      }
    }
  },
  computed: {
    isAllowed() {
      const user = firebase.auth().currentUser
      return user && user.uid === 'rdcfVYk5wweay8cwYVSNZyWHztX2'
    },
    // ห้ามตั้งวันหมดอายุย้อนหลัง — บริษัทจะถูกบล็อกทันทีที่สร้างเสร็จ
    minExpireDate() {
      return bangkokToday()
    },
    expireHint() {
      const days = labLicenseDaysLeft(this.form.labExpireDate)
      if (days === null) return 'เลือกวันหมดอายุ license ของบริษัทชุดนี้'
      const until = formatLabExpireDate(this.form.labExpireDate)
      return `ใช้งานได้ถึงสิ้นวันที่ ${until} (อีก ${days} วัน) หลังจากนั้นเข้าใช้งานบริษัทไม่ได้`
    },
  },
  methods: {
    toThreeDigits(num) {
      return String(num).padStart(3, '0')
    },

    setExpireInMonths(months) {
      this.form.labExpireDate = addMonthsToDateString(bangkokToday(), months)
    },

    async validateViaAPI(field) {
      // Handle 'all' field case
      if (field === 'all') {
        // Set loading state for all fields that have values
        if (this.form.companyPrefix) {
          this.validating.companyPrefix = true
          this.validationErrors.companyPrefix = null
        }
        if (this.form.userPrefix) {
          this.validating.userPrefix = true
          this.validationErrors.userPrefix = null
        }
        if (this.form.userEmailPrefix) {
          this.validating.userEmailPrefix = true
          this.validationErrors.userEmailPrefix = null
        }
      } else {
        // If no prefix is provided for the specific field, clear validation
        if (field === 'companyPrefix' && !this.form.companyPrefix) {
          this.validationErrors.companyPrefix = null
          this.validating.companyPrefix = false
          return
        }
        if (field === 'userPrefix' && !this.form.userPrefix) {
          this.validationErrors.userPrefix = null
          this.validating.userPrefix = false
          return
        }
        if (field === 'userEmailPrefix' && !this.form.userEmailPrefix) {
          this.validationErrors.userEmailPrefix = null
          this.validating.userEmailPrefix = false
          return
        }

        // Set loading state for the specific field
        if (field === 'companyPrefix') {
          this.validating.companyPrefix = true
          this.validationErrors.companyPrefix = null
        } else if (field === 'userPrefix') {
          this.validating.userPrefix = true
          this.validationErrors.userPrefix = null
        } else if (field === 'userEmailPrefix') {
          this.validating.userEmailPrefix = true
          this.validationErrors.userEmailPrefix = null
        }
      }

      try {
        const idToken = await firebase.auth().currentUser.getIdToken()
        const res = await fetch(fc.cloudFunctionUrl + 'apiLaboratoryValidate', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json', 
            Authorization: `Bearer ${idToken}` 
          },
          body: JSON.stringify({
            companyPrefix: this.form.companyPrefix || '',
            userPrefix: this.form.userPrefix || '',
            userEmailPrefix: this.form.userEmailPrefix || '',
            startIndex: this.form.startIndex,
            count: this.form.count,
          }),
        })

        const json = await res.json()
        
        if (!json.success) {
          throw new Error(json.message || 'Validation failed')
        }

        const { results } = json

        // Update Company ID validation
        if (field === 'companyPrefix' || field === 'all') {
          if (results.companyIds && results.companyIds.duplicates.length > 0) {
            this.validationErrors.companyPrefix = `พบ Company ID ที่มีอยู่แล้ว: ${results.companyIds.duplicates.join(', ')}`
          } else if (this.form.companyPrefix) {
            this.validationErrors.companyPrefix = true
          }
        }

        // Update User ID validation
        if (field === 'userPrefix' || field === 'all') {
          if (results.userIds && results.userIds.duplicates.length > 0) {
            this.validationErrors.userPrefix = `พบ User ID ที่มีอยู่แล้ว: ${results.userIds.duplicates.join(', ')}`
          } else if (this.form.userPrefix) {
            this.validationErrors.userPrefix = true
          }
        }

        // Update Email validation
        if (field === 'userEmailPrefix' || field === 'all') {
          if (results.emails && results.emails.duplicates.length > 0) {
            this.validationErrors.userEmailPrefix = `พบ Email ที่มีอยู่แล้ว: ${results.emails.duplicates.join(', ')}`
          } else if (this.form.userEmailPrefix) {
            this.validationErrors.userEmailPrefix = true
          }
        }
      } catch (error) {
        console.error(`Error validating ${field}:`, error)
        const errorMsg = 'เกิดข้อผิดพลาดในการตรวจสอบ'
        if (field === 'companyPrefix' || field === 'all') {
          this.validationErrors.companyPrefix = errorMsg
        }
        if (field === 'userPrefix' || field === 'all') {
          this.validationErrors.userPrefix = errorMsg
        }
        if (field === 'userEmailPrefix' || field === 'all') {
          this.validationErrors.userEmailPrefix = errorMsg
        }
      } finally {
        // Clear loading states
        if (field === 'companyPrefix' || field === 'all') {
          this.validating.companyPrefix = false
        }
        if (field === 'userPrefix' || field === 'all') {
          this.validating.userPrefix = false
        }
        if (field === 'userEmailPrefix' || field === 'all') {
          this.validating.userEmailPrefix = false
        }

        // Trigger form validation update
        this.$nextTick(() => {
          if (this.$refs.form) {
            this.$refs.form.validate()
          }
        })
      }
    },

    async validateCompanyPrefix() {
      await this.validateViaAPI('companyPrefix')
    },

    async validateUserPrefix() {
      await this.validateViaAPI('userPrefix')
    },

    async validateUserEmailPrefix() {
      await this.validateViaAPI('userEmailPrefix')
    },

    debounceValidateCompanyPrefix() {
      if (this.debounceTimers.companyPrefix) {
        clearTimeout(this.debounceTimers.companyPrefix)
      }
      this.debounceTimers.companyPrefix = setTimeout(() => {
        this.validateCompanyPrefix()
      }, 500)
    },

    debounceValidateUserPrefix() {
      if (this.debounceTimers.userPrefix) {
        clearTimeout(this.debounceTimers.userPrefix)
      }
      this.debounceTimers.userPrefix = setTimeout(() => {
        this.validateUserPrefix()
      }, 500)
    },

    debounceValidateUserEmailPrefix() {
      if (this.debounceTimers.userEmailPrefix) {
        clearTimeout(this.debounceTimers.userEmailPrefix)
      }
      this.debounceTimers.userEmailPrefix = setTimeout(() => {
        this.validateUserEmailPrefix()
      }, 500)
    },

    debounceValidateAll() {
      // Clear all existing timers
      Object.values(this.debounceTimers).forEach(timer => {
        if (timer) clearTimeout(timer)
      })
      
      // Set a single timer to validate all fields
      const timer = setTimeout(() => {
        this.validateViaAPI('all')
      }, 500)
      
      // Store timer (we can use any field's timer slot)
      this.debounceTimers.companyPrefix = timer
    },

    async startRun() {
      // Validate all fields via API before running
      await this.validateViaAPI('all')

      if (!this.$refs.form || !this.$refs.form.validate()) return
      
      // Check validation errors for Company ID, User ID, and Email only
      const hasCompanyError = this.validationErrors.companyPrefix !== null && this.validationErrors.companyPrefix !== true
      const hasUserError = this.validationErrors.userPrefix !== null && this.validationErrors.userPrefix !== true
      const hasEmailError = this.form.userEmailPrefix && this.validationErrors.userEmailPrefix !== null && this.validationErrors.userEmailPrefix !== true
      
      if (hasCompanyError || hasUserError || hasEmailError) {
        alert('กรุณาแก้ไขข้อมูลที่ซ้ำก่อนดำเนินการต่อ')
        return
      }

      this.loading = true
      try {
        this.runId = `${Date.now()}`
        // subscribe progress under Users/{uid}/LaboratoryRuns/{runId}
        this.unsubscribe && this.unsubscribe()
        const uid = firebase.auth().currentUser.uid
        this.unsubscribe = fc.db.collection('Users').doc(uid).collection('LaboratoryRuns').doc(this.runId).onSnapshot(s => {
          if (s.exists) this.progress = s.data() || {}
        })

        const idToken = await firebase.auth().currentUser.getIdToken()
        const res = await fetch(fc.cloudFunctionUrl + 'apiLaboratoryRun', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({ runId: this.runId, ...this.form }),
        })
        const json = await res.json()
        if (!json.success) throw new Error(json.message || 'run failed')
      } catch (e) {
        alert('เริ่มงานไม่สำเร็จ: ' + e.message)
      } finally {
        this.loading = false
      }
    }
  },
  watch: {
    'form.startIndex'() {
      // Re-validate all fields when startIndex changes
      this.debounceValidateAll()
    },
    'form.count'() {
      // Re-validate all fields when count changes
      this.debounceValidateAll()
    }
  },
  beforeDestroy() {
    this.unsubscribe && this.unsubscribe()
    // Clear debounce timers
    Object.values(this.debounceTimers).forEach(timer => {
      if (timer) clearTimeout(timer)
    })
  }
}
</script>

<style scoped>
</style>


